/**
 * The one stale-bundle monitor the app runs, wired to real `fetch` and Faro.
 *
 * Kept apart from `staleBundleMonitor.ts` so the monitor itself stays free of
 * the Faro/Clerk import graph and is unit-testable on its own.
 */

import { reportFaroEvent } from 'src/lib/grafana';
import { createServedBuildSignatureReader } from './buildSignature';
import { createStaleBundleMonitor, type StaleBundleDetail, type StaleBundleMonitor } from './staleBundleMonitor';

/** The Faro event name to alert and dashboard on. */
export const STALE_BUNDLE_FARO_EVENT = 'web_bundle_stale';

/** Faro attributes are strings on the wire; a signature lists every hashed asset. */
const MAX_SIGNATURE_ATTRIBUTE_LENGTH = 256;

const forAttribute = (signature: string) => signature.slice(0, MAX_SIGNATURE_ATTRIBUTE_LENGTH);

export function reportStaleBundle({ runningSignature, servedSignature }: StaleBundleDetail) {
  reportFaroEvent(STALE_BUNDLE_FARO_EVENT, {
    build_version: process.env.RENDER_GIT_COMMIT ?? 'unknown',
    running_bundle: forAttribute(runningSignature),
    served_bundle: forAttribute(servedSignature),
  });

  // Second channel: Faro captures console in deployed builds, and this is the
  // only trace of the condition when running locally.
  console.warn('Running a stale web bundle; the server is serving a newer one', { runningSignature, servedSignature });
}

let appMonitor: StaleBundleMonitor | null = null;

/** Created lazily, so importing this module performs no IO and starts no timer. */
export function getAppStaleBundleMonitor(): StaleBundleMonitor {
  appMonitor ??= createStaleBundleMonitor({
    onStale: reportStaleBundle,
    readServedSignature: createServedBuildSignatureReader(),
  });

  return appMonitor;
}
